import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { formatDate } from '../utils/formatting';
import { getDefaultSettings, updateSettings } from '../utils/tenantSettings';
import {
  tenantsApi,
  organizationsApi,
  paymentMethodsApi,
  Tenant,
  Organization,
  PaymentMethod,
  CreatePaymentMethodDto,
  UpdatePaymentMethodDto,
} from '../utils/api';

export const TenantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreatePaymentMethodDto>({
    tenant_id: id || '',
    payment_method_name: 'paypal',
    display_name: '',
    description: '',
    settings: {},
    display_order: 0,
    is_active: true,
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [originalReferencePrefix, setOriginalReferencePrefix] = useState<string>('');

  useEffect(() => {
    if (id && token) {
      fetchTenantDetails();
      fetchPaymentMethods();
    }
  }, [id, token]);

  const fetchTenantDetails = async () => {
    if (!id || !token) return;

    try {
      const data = await tenantsApi.getOne(token, id);
      setTenant(data);

      // Fetch organization details
      if (data.organization_id) {
        try {
          const org = await organizationsApi.getOne(token, data.organization_id);
          setOrganization(org);
        } catch (err) {
          console.error('Failed to fetch organization details', err);
        }
      }
    } catch (err) {
      setError(t('tenantDetails.failedToLoadDetails'));
    }
  };

  const fetchPaymentMethods = async () => {
    if (!id || !token) return;

    try {
      setLoading(true);
      const data = await paymentMethodsApi.getByTenant(token, id);
      setPaymentMethods(data);
      setError('');
    } catch (err) {
      setError(t('tenantDetails.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      tenant_id: id || '',
      payment_method_name: 'paypal',
      display_name: '',
      description: '',
      settings: getDefaultSettings('paypal'),
      display_order: 0,
      is_active: true,
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const openEditModal = (paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setFormData({
      tenant_id: paymentMethod.tenant_id,
      payment_method_name: paymentMethod.payment_method_name,
      display_name: paymentMethod.display_name || '',
      description: paymentMethod.description || '',
      settings: paymentMethod.settings || {},
      display_order: paymentMethod.display_order,
      is_active: paymentMethod.is_active,
    });
    setOriginalReferencePrefix(paymentMethod.settings?.reference_prefix || '');
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setFormError('');
    setShowDeleteModal(true);
  };

  const handleCreatePaymentMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormLoading(true);
    setFormError('');

    try {
      await paymentMethodsApi.create(token, formData);
      setShowCreateModal(false);
      await fetchPaymentMethods();
    } catch (err: any) {
      setFormError(err.message || t('tenantDetails.failedToCreate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditPaymentMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedPaymentMethod) return;

    setFormLoading(true);
    setFormError('');

    try {
      const updateData: UpdatePaymentMethodDto = {
        payment_method_name: formData.payment_method_name,
        display_name: formData.display_name,
        description: formData.description,
        settings: formData.settings,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };
      await paymentMethodsApi.update(token, selectedPaymentMethod.payment_method_id, updateData);
      setShowEditModal(false);
      setSelectedPaymentMethod(null);
      await fetchPaymentMethods();
    } catch (err: any) {
      setFormError(err.message || t('tenantDetails.failedToUpdate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePaymentMethod = async () => {
    if (!token || !selectedPaymentMethod) return;

    setFormLoading(true);
    setFormError('');

    try {
      await paymentMethodsApi.delete(token, selectedPaymentMethod.payment_method_id);
      setShowDeleteModal(false);
      setSelectedPaymentMethod(null);
      await fetchPaymentMethods();
    } catch (err: any) {
      setFormError(err.message || t('tenantDetails.failedToDelete'));
    } finally {
      setFormLoading(false);
    }
  };

  const renderPaymentMethodFields = () => {
    if (formData.payment_method_name === 'paypal') {
      return (
        <>
          <h4 className="font-medium text-gray-900 mb-3">API Settings</h4>
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client ID *</label>
              <input
                type="text"
                required
                value={formData.settings?.client_id || ''}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'client_id', e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                placeholder="Your PayPal Client ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret *</label>
              <input
                type="password"
                required
                value={formData.settings?.client_secret || ''}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'client_secret', e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                placeholder="Your PayPal Client Secret"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode *</label>
              <select
                value={formData.settings?.mode || 'sandbox'}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'mode', e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              >
                <option value="sandbox">Sandbox</option>
                <option value="live">Live</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Webhook ID</label>
              <input
                type="text"
                value={formData.settings?.webhook_id || ''}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'webhook_id', e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                placeholder="PayPal Webhook ID for signature verification"
              />
            </div>
          </div>

          <h4 className="font-medium text-gray-900 mb-3">Display Settings</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Style</label>
              <select
                value={formData.settings?.button_style || 'gold'}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'button_style', e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              >
                <option value="gold">Gold</option>
                <option value="blue">Blue</option>
                <option value="silver">Silver</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enable_venmo"
                checked={formData.settings?.enable_venmo || false}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'enable_venmo', e.target.checked) })}
                className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
              />
              <label htmlFor="enable_venmo" className="ml-2 text-sm text-gray-700">
                Enable Venmo
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enable_credit_card"
                checked={formData.settings?.enable_credit_card || false}
                onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'enable_credit_card', e.target.checked) })}
                className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
              />
              <label htmlFor="enable_credit_card" className="ml-2 text-sm text-gray-700">
                Allow credit card without PayPal account
              </label>
            </div>
          </div>
        </>
      );
    } else if (formData.payment_method_name === 'direct_deposit') {
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IBAN *</label>
            <input
              type="text"
              required
              value={formData.settings?.iban || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'iban', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="DE89 3704 0044 0532 0130 00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary Name *</label>
            <input
              type="text"
              required
              value={formData.settings?.beneficiary_name || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'beneficiary_name', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="Company Name or Individual"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BIC</label>
            <input
              type="text"
              value={formData.settings?.bic || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'bic', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="COBADEFFXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
            <input
              type="text"
              value={formData.settings?.bank_name || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'bank_name', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="Deutsche Bank AG"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Address</label>
            <textarea
              value={formData.settings?.bank_address || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'bank_address', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="Street, City, Country"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference Prefix *</label>
            <input
              type="text"
              required
              value={formData.settings?.reference_prefix || ''}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'reference_prefix', e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
              placeholder="INV-"
            />
            {showEditModal && originalReferencePrefix && formData.settings?.reference_prefix !== originalReferencePrefix && (
              <p className="mt-1 text-sm text-amber-600">
                Warning: changing this prefix will break matching for existing invoices that used "{originalReferencePrefix}".
              </p>
            )}
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="qr_code_enabled"
              checked={formData.settings?.qr_code_enabled || false}
              onChange={(e) => setFormData({ ...formData, settings: updateSettings(formData.settings, 'qr_code_enabled', e.target.checked) })}
              className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
            />
            <label htmlFor="qr_code_enabled" className="ml-2 text-sm text-gray-700">
              Enable EPC QR Code generation
            </label>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!tenant && !loading) {
    return (
      <AdminLayout title={t('tenantDetails.notFound')} subtitle={t('tenantDetails.notFoundSubtitle')}>
        <div className="text-center py-16">
          <p className="text-red-600 mb-4">{t('tenantDetails.tenantNotFound')}</p>
          <button
            onClick={() => navigate('/admin/tenants')}
            className="text-[var(--navy)] hover:text-[var(--navy-light)] cursor-pointer"
          >
            {t('tenantDetails.backToTenants')}
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={organization?.name || t('tenantDetails.title')}
      subtitle={organization?.code || t('common.loading')}
    >
      <div className="space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="flex items-center gap-2 text-[var(--navy)] hover:text-[var(--navy-light)] cursor-pointer"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            {t('tenantDetails.backToTenants')}
          </button>
        </div>

        {/* Tenant Information */}
        {tenant && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex items-start gap-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gold-pale)' }}
                >
                  <span className="font-bold text-2xl" style={{ color: 'var(--navy)' }}>
                    {(organization?.name || 'T').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{organization?.name || 'Loading...'}</h2>
                  <p className="text-gray-500 mt-1">{organization?.code}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tenant.is_active
                          ? 'bg-[var(--success-bg)] text-[var(--success)]'
                          : 'bg-[var(--error-bg)] text-[var(--error)]'
                      }`}
                    >
                      {tenant.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                    <span className="text-sm text-gray-500">
                      {t('tenantDetails.invoicePrefix')}: {tenant.invoice_prefix}
                    </span>
                    <span className="text-sm text-gray-500">
                      {t('tenantDetails.created', { date: formatDate(tenant.created_at) })}
                    </span>
                  </div>
                </div>
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="rounded-lg" style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}>
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">{t('tenantDetails.paymentMethods')}</h3>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 bg-[var(--navy)] text-white px-4 py-2 rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors cursor-pointer"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {t('tenantDetails.addPaymentMethod')}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-10 h-10 border-3 border-gray-200 border-t-[var(--gold)] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500">{t('tenantDetails.loadingPaymentMethods')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t('tenantDetails.method')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t('tenantDetails.displayName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t('common.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t('tenantDetails.order')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paymentMethods.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        {t('tenantDetails.noPaymentMethods')}
                      </td>
                    </tr>
                  ) : (
                    paymentMethods.map((method) => (
                      <tr key={method.payment_method_id} className="hover:bg-[var(--gold-pale)]">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {method.payment_method_name}
                          </div>
                          {method.description && (
                            <div className="text-sm text-gray-500">{method.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{method.display_name || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              method.is_active
                                ? 'bg-[var(--success-bg)] text-[var(--success)]'
                                : 'bg-[var(--error-bg)] text-[var(--error)]'
                            }`}
                          >
                            {method.is_active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {method.display_order}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => openEditModal(method)}
                            className="text-[var(--navy)] hover:text-[var(--navy-light)] mr-4 cursor-pointer"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => openDeleteModal(method)}
                            className="text-red-600 hover:text-red-900 cursor-pointer"
                          >
                            {t('common.delete')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add Payment Method</h3>
            <form onSubmit={handleCreatePaymentMethod}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method Type *
                  </label>
                  <select
                    value={formData.payment_method_name}
                    onChange={(e) => setFormData({ ...formData, payment_method_name: e.target.value, settings: getDefaultSettings(e.target.value, formData.settings) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  >
                    <option value="paypal">PayPal</option>
                    <option value="direct_deposit">Direct Deposit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="e.g., PayPal Checkout"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  />
                </div>

                <div className="border-t border-gray-200 pt-4">
                  {renderPaymentMethodFields()}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_create"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                  />
                  <label htmlFor="is_active_create" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              {formError && (
                <div className="mt-4 text-red-600 text-sm">{formError}</div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors cursor-pointer"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50 cursor-pointer"
                  disabled={formLoading}
                >
                  {formLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPaymentMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Edit Payment Method</h3>
            <form onSubmit={handleEditPaymentMethod}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method Type *
                  </label>
                  <select
                    value={formData.payment_method_name}
                    onChange={(e) => setFormData({ ...formData, payment_method_name: e.target.value, settings: getDefaultSettings(e.target.value, formData.settings) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  >
                    <option value="paypal">PayPal</option>
                    <option value="direct_deposit">Direct Deposit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="e.g., PayPal Checkout"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  />
                </div>

                <div className="border-t border-gray-200 pt-4">
                  {renderPaymentMethodFields()}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_edit"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                  />
                  <label htmlFor="is_active_edit" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              {formError && (
                <div className="mt-4 text-red-600 text-sm">{formError}</div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors cursor-pointer"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50 cursor-pointer"
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedPaymentMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Payment Method</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the payment method <strong>{selectedPaymentMethod.payment_method_name}</strong>? This action will soft delete the payment method.
            </p>
            {formError && (
              <div className="mb-4 text-red-600 text-sm">{formError}</div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors cursor-pointer"
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePaymentMethod}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer"
                disabled={formLoading}
              >
                {formLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};
